import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const WebSocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activities, setActivities] = useState(new Map());
  const socketRef = useRef(null);
  const tokenRef = useRef(token);
  const userIdRef = useRef(user?.id);

  // Keep refs in sync without triggering reconnects
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { userIdRef.current = user?.id; }, [user?.id]);

  // Récupérer la liste des utilisateurs en ligne
  const fetchOnlineUsers = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (!currentToken) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/realtime/online-users`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setOnlineUsers(data.online_users || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs en ligne:', error);
    }
  }, []);

  // Ajouter une notification
  const addNotification = useCallback((type, message, persistent = false, data = null, duration = 5000) => {
    const notification = {
      id: Date.now() + Math.random(),
      type,
      message,
      persistent,
      data,
      timestamp: new Date().toISOString(),
      read: false
    };

    setNotifications(prev => [notification, ...prev.slice(0, 49)]);

    if (!persistent) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }, duration);
    }
  }, []);

  // Supprimer une notification
  const removeNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Marquer une notification comme lue
  const markNotificationAsRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  }, []);

  // Envoyer l'activité utilisateur
  const updateUserActivity = useCallback((type, resourceType = null, resourceId = null, page = null) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('user_activity', {
        type,
        resource_type: resourceType,
        resource_id: resourceId,
        page
      });
    }
  }, []);

  // Rejoindre une room spécifique à une ressource
  const joinResourceRoom = useCallback((resourceType, resourceId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join_resource_room', {
        resource_type: resourceType,
        resource_id: resourceId
      });
    }
  }, []);

  // Quitter une room spécifique à une ressource
  const leaveResourceRoom = useCallback((resourceType, resourceId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_resource_room', {
        resource_type: resourceType,
        resource_id: resourceId
      });
    }
  }, []);

  // Diffuser un message admin
  const broadcastMessage = useCallback(async (title, message) => {
    const currentToken = tokenRef.current;
    if (!currentToken) return false;

    try {
      const response = await fetch(`${BACKEND_URL}/api/realtime/broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`
        },
        body: JSON.stringify({ title, message })
      });

      return response.ok;
    } catch (error) {
      console.error('Erreur lors de la diffusion:', error);
      return false;
    }
  }, []);

  // Single connection effect — only triggers on actual user login/logout
  useEffect(() => {
    if (!token || !user?.id) {
      // Cleanup on logout
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        setOnlineUsers([]);
        setActivities(new Map());
      }
      return;
    }

    // Don't reconnect if already connected with same user
    if (socketRef.current?.connected) {
      return;
    }

    // Cleanup any stale socket
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const newSocket = io(BACKEND_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 15000,
      reconnectionAttempts: 10,
    });

    socketRef.current = newSocket;

    newSocket.on('connect', () => {
      setIsConnected(true);
      setSocket(newSocket);
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
      // Socket.IO handles reconnection automatically with config above
    });

    // Événements temps réel
    newSocket.on('user_connected', (data) => {
      addNotification('info', `${data.email} s'est connecté`, false);
      fetchOnlineUsers();
    });

    newSocket.on('user_disconnected', (data) => {
      addNotification('info', `${data.email} s'est déconnecté`, false);
      fetchOnlineUsers();
    });

    newSocket.on('user_activity_update', (activity) => {
      setActivities(prev => new Map(prev.set(activity.user_id, activity)));
    });

    newSocket.on('package_created', (data) => {
      addNotification('success',
        `Nouveau colis créé: ${data.oce_code}`,
        true,
        { type: 'package', id: data.package_id }
      );
    });

    newSocket.on('package_status_updated', (data) => {
      addNotification('info',
        `Colis ${data.oce_code}: ${data.old_status} → ${data.new_status}`,
        true,
        { type: 'package', id: data.package_id }
      );
    });

    newSocket.on('packages_assigned_to_shipment', (data) => {
      addNotification('success',
        `${data.collected_count} colis assignés à ${data.shipment_name}`,
        true,
        { type: 'shipment', id: data.shipment_id }
      );
    });

    newSocket.on('admin_broadcast', (data) => {
      addNotification('warning',
        `📢 ${data.title}: ${data.message}`,
        true,
        null,
        10000
      );
    });

    newSocket.on('personal_notification', (data) => {
      addNotification(data.type || 'info',
        `${data.title}: ${data.message}`,
        true,
        data.data
      );
    });

    newSocket.on('resource_updated', (data) => {
      window.dispatchEvent(new CustomEvent('resource_updated', { detail: data }));
    });

    newSocket.on('shipment_status_updated', (data) => {
      addNotification('info',
        `Carreaison ${data.shipment_name}: ${data.old_status} → ${data.new_status}`,
        true,
        { type: 'shipment', id: data.shipment_id }
      );
      window.dispatchEvent(new CustomEvent('shipment_status_updated', { detail: data }));
    });

    newSocket.on('qr_submission_received', (data) => {
      addNotification('info',
        `Nouvelle soumission QR de ${data.sender_name || 'un client'}`,
        true,
        data
      );
      window.dispatchEvent(new CustomEvent('qr_submission_received', { detail: data }));
    });

    // Fetch initial online users
    setTimeout(fetchOnlineUsers, 1000);

    return () => {
      newSocket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    };
  }, [user?.id]); // eslint-disable-line

  const value = {
    socket,
    isConnected,
    onlineUsers,
    notifications,
    activities: Array.from(activities.values()),
    addNotification,
    removeNotification,
    markNotificationAsRead,
    updateUserActivity,
    joinResourceRoom,
    leaveResourceRoom,
    broadcastMessage,
    fetchOnlineUsers
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
